import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Alert,
  Button,
  CircularProgress,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import React, {useCallback, useEffect, useState} from 'react';
import '../../App.css';
import FirestoreManager from '../../api/firebaseManager';
import {useAuth} from '../../hooks/use-auth';
import {Modal} from '../modal/modal';

// FOR-2250: intake window for the direct custodian feeds. These custodians
// have no API and no per-customer SFTP, so their files land on the data feed
// server and a conversion script turns them into Forest format. That script
// needs to know which Forest entity owns which account number, and this is
// where a user records it. Read back by the FOR-2252 endpoint.
//
// Deliberately generic: the custodian is a prop, so adding Goldman Sachs or
// any future broker needs a card on the Add Connection page and nothing here.

const buttonStyle = {
  zIndex: 1,
  backgroundColor: '#c5c7c6',
  height: '30px',
  border: '0px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0px 32px',
  margin: '16px 8px 0px 8px',
  color: 'black',
};

// A Cloud Function that throws reaches the browser as the bare word "internal",
// which tells the user nothing and sent us chasing a phantom bug once already.
const describeError = (err, fallback) => {
  const message = err?.message || '';
  if (!message || message.toLowerCase() === 'internal') {
    return `${fallback}. Something went wrong on our side, please try again.`;
  }
  return message;
};

const emptyRow = () => ({entity_name: '', account_number: ''});

export const CustodianModal = ({active, setActive, custodian, displayName}) => {
  const {
    state: {profile},
  } = useAuth();
  const customerID = profile?.customerID;

  const [entities, setEntities] = useState([]);
  // The Forest entity endpoint is not available in every environment. Rather
  // than block the whole window, fall back to a free text entity field.
  const [entitiesUnavailable, setEntitiesUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([emptyRow()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saved, setSaved] = useState(false);

  // Load the entity list and whatever the customer saved previously, so
  // reopening the window edits the existing mappings rather than starting over.
  const load = useCallback(async () => {
    // loading starts true, so returning early here without clearing it left the
    // window spinning forever whenever the profile had no customerID.
    if (!customerID || !custodian) {
      setLoading(false);
      setError(
        !customerID ?
          'Your profile has no customer id, so accounts cannot be loaded. ' +
            'Try signing out and back in, or contact Forest Support.' :
          'No custodian selected.',
      );
      return;
    }
    setLoading(true);
    setError('');
    try {
      const [entityRes, mappingRes] = await Promise.all([
        FirestoreManager.listCustodianEntities({customerID}),
        FirestoreManager.getCustodianMappings({customerID, custodian}),
      ]);

      if (entityRes?.unavailable) {
        setEntitiesUnavailable(true);
        setEntities([]);
      } else if (entityRes?.success) {
        setEntities(entityRes.entities || []);
      } else if (entityRes?.error) {
        setError(entityRes.error);
      }

      const existing = mappingRes?.data?.[0]?.mappings || [];
      setRows(existing.length ? existing.map(m => ({...m})) : [emptyRow()]);
    } catch (err) {
      setError(describeError(err, 'Could not load this connection'));
    } finally {
      setLoading(false);
    }
  }, [customerID, custodian]);

  useEffect(() => {
    if (active) {
      setSaved(false);
      load();
    }
  }, [active, load]);

  const updateRow = (index, field, value) => {
    setRows(current =>
      current.map((row, i) => (i === index ? {...row, [field]: value} : row)),
    );
    setError('');
  };

  const addRow = () => setRows(current => [...current, emptyRow()]);

  const removeRow = index => {
    setRows(current => {
      const next = current.filter((row, i) => i !== index);
      // Always leave one row so the window never looks broken when emptied.
      return next.length ? next : [emptyRow()];
    });
    setError('');
  };

  // Mirrors the backend's rules so a user sees the problem next to the field
  // instead of after a round trip.
  const validate = () => {
    const filled = rows.filter(
      row => row.entity_name?.trim() || row.account_number?.trim(),
    );
    if (!filled.length) {
      return 'Add at least one entity and account number.';
    }
    for (const row of filled) {
      if (!row.entity_name?.trim() || !row.account_number?.trim()) {
        return 'Every row needs both an entity and an account number.';
      }
    }
    const numbers = filled.map(row => row.account_number.trim());
    const duplicate = numbers.find(
      (n, i) => numbers.indexOf(n) !== i,
    );
    if (duplicate) {
      return `Account number ${duplicate} appears more than once.`;
    }
    return '';
  };

  const save = async () => {
    const problem = validate();
    if (problem) {
      setError(problem);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = rows
        .filter(row => row.entity_name?.trim() && row.account_number?.trim())
        .map(row => ({
          entity_name: row.entity_name.trim(),
          account_number: row.account_number.trim(),
        }));

      const res = await FirestoreManager.saveCustodianMappings({
        customerID,
        custodian,
        mappings: payload,
      });

      if (res?.success) {
        setSaved(true);
      } else {
        setError(res?.error || 'Could not save these accounts');
      }
    } catch (err) {
      setError(describeError(err, 'Could not save these accounts'));
    } finally {
      setSaving(false);
    }
  };

  const savedCount = rows.filter(
    row => row.entity_name?.trim() && row.account_number?.trim(),
  ).length;

  return (
    <Modal active={active} setActive={setActive}>
      <div className="box flex-column" style={{padding: '8px 4px'}}>
        <Typography variant="h6" style={{marginBottom: 4}}>
          Connect {displayName}
        </Typography>

        {saved ? (
          <div style={{padding: '8px 0 4px'}}>
            <Alert severity="success" style={{marginBottom: 12}}>
              {savedCount} account{savedCount === 1 ? '' : 's'} connected.
            </Alert>
            <Typography variant="body2" style={{color: '#4b544d'}}>
              Forest Support will be in touch with authorization forms to
              complete the connection.
            </Typography>
            <div style={{display: 'flex', justifyContent: 'flex-end'}}>
              <Button style={buttonStyle} onClick={() => setActive(false)}>
                Done
              </Button>
            </div>
          </div>
        ) : (
          <>
            <Typography
              variant="body2"
              style={{color: '#4b544d', marginBottom: 14}}>
              Map each Forest entity to its account. Add a row for every account
              you would like to connect.
            </Typography>

            {loading ? (
              <div style={{display: 'flex', justifyContent: 'center', padding: 24}}>
                <CircularProgress size={26} />
              </div>
            ) : (
              <>
                {!entitiesUnavailable && !entities.length && (
                  <Alert severity="warning" style={{marginBottom: 12}}>
                    No Forest entities were found for this customer, so there is
                    nothing to map accounts to yet. Contact Forest Support if
                    you expect entities here.
                  </Alert>
                )}

                {entitiesUnavailable && (
                  <Alert severity="info" style={{marginBottom: 12}}>
                    The Forest entity list is unavailable, so entity names must
                    be typed in. Check the spelling matches Forest exactly.
                  </Alert>
                )}

                <div style={{maxHeight: 300, overflowY: 'auto', paddingRight: 4}}>
                  {rows.map((row, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        gap: 8,
                        alignItems: 'center',
                        marginBottom: 10,
                      }}>
                      {entitiesUnavailable ? (
                        <TextField
                          size="small"
                          label="Forest entity"
                          value={row.entity_name || ''}
                          onChange={e =>
                            updateRow(index, 'entity_name', e.target.value)
                          }
                          style={{flex: 1}}
                        />
                      ) : (
                        <TextField
                          select
                          size="small"
                          label="Forest entity"
                          value={row.entity_name || ''}
                          onChange={e =>
                            updateRow(index, 'entity_name', e.target.value)
                          }
                          style={{flex: 1}}>
                          {/* A previously saved entity may no longer be in the
                              active list; keep it selectable so opening the
                              window does not silently blank the row. */}
                          {row.entity_name &&
                            !entities.some(e => e.name === row.entity_name) && (
                            <MenuItem value={row.entity_name}>
                              {row.entity_name}
                            </MenuItem>
                          )}
                          {entities.map(entity => (
                            <MenuItem key={entity.id} value={entity.name}>
                              {entity.name}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}

                      <TextField
                        size="small"
                        label="Account number"
                        placeholder="e.g. 8834021"
                        value={row.account_number || ''}
                        onChange={e =>
                          updateRow(index, 'account_number', e.target.value)
                        }
                        style={{flex: 1}}
                      />

                      <IconButton
                        aria-label="Remove row"
                        size="small"
                        onClick={() => removeRow(index)}>
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </div>
                  ))}
                </div>

                <Button
                  onClick={addRow}
                  style={{
                    alignSelf: 'flex-start',
                    textTransform: 'none',
                    color: '#2c6146',
                  }}>
                  + Add another entity
                </Button>

                {error && (
                  <Alert severity="error" style={{marginTop: 10}}>
                    {error}
                  </Alert>
                )}

                <Typography
                  variant="caption"
                  style={{color: '#4b544d', marginTop: 12}}>
                  Expect Forest Support to reach out to you with authorization
                  forms to complete.
                </Typography>

                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    marginTop: 4,
                  }}>
                  <Button style={buttonStyle} onClick={() => setActive(false)}>
                    Cancel
                  </Button>
                  <Button style={buttonStyle} onClick={save} disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                  </Button>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};
